/**
 * Paginador de un buscador (pedido de la dueña 2026-10-20): los buscadores de productos mostraban solo los primeros 12
 * (o 8) resultados y no había forma de ver el resto. Dice cuántos hay y deja pasar de página. La página vuelve a la 1
 * cada vez que cambia lo que se busca (`usePaginaBusqueda`).
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, type MouseEvent } from 'react';
import { Button } from '@/componentes/ui/button.tsx';

/** Página actual de un buscador: vuelve a la 1 cuando cambia `clave` (lo que se busca, la lista de precios…). */
export function usePaginaBusqueda(clave: string): [number, (n: number) => void] {
  const [estado, setEstado] = useState<{ clave: string; pagina: number }>({ clave, pagina: 1 });
  // La vuelta a la 1 se guarda (no solo se calcula): buscar «pego», ir a la 2, buscar «pegos» y volver a «pego» arranca en
  // la 1, no en la 2 de antes (lo encontró la prueba visual `buscadores`)
  if (estado.clave !== clave) setEstado({ clave, pagina: 1 });
  const pagina = estado.clave === clave ? estado.pagina : 1;
  return [pagina, (n: number) => setEstado({ clave, pagina: Math.max(1, n) })];
}

/**
 * ¿Los resultados en pantalla son de esta misma búsqueda? Mientras llega otra página, React Query deja la anterior
 * (`isPlaceholderData`): si lo buscado no cambió se sigue mostrando, sin parpadeo; si cambió, no (que nadie elija un
 * resultado de otra búsqueda). `cargada` = llegó la respuesta de la búsqueda actual (`!!data && !isPlaceholderData`).
 */
export function useMismaBusqueda(clave: string, cargada: boolean): boolean {
  const [vista, setVista] = useState(clave);
  if (cargada && vista !== clave) setVista(clave);
  return cargada || vista === clave;
}

interface Props {
  pagina: number;
  limite: number;
  total: number;
  onPagina: (n: number) => void;
  /** Prefijo de los data-testid (`<prefijo>-paginador`, `-anterior`, `-siguiente`). */
  testid?: string;
  className?: string;
  /** Botones de 40 px para tocarlos con el dedo (el POS, reglas de diseño MG). */
  grande?: boolean;
}

/** El clic no le quita el foco al buscador: no se cierra el desplegable y el POS sigue con el cursor en la búsqueda. */
const sinRobarFoco = (e: MouseEvent) => e.preventDefault();

/** «1–12 de 340» con Anterior y Siguiente; no aparece si todo cabe en una página. */
export function PaginadorBusqueda({ pagina, limite, total, onPagina, testid = 'buscador', className = '', grande = false }: Props) {
  if (total <= limite) return null;
  const paginas = Math.ceil(total / limite);
  const desde = (pagina - 1) * limite + 1;
  const hasta = Math.min(pagina * limite, total);
  return (
    <div className={`flex items-center justify-between gap-2 border-t bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground ${className}`} data-testid={`${testid}-paginador`}>
      <span className="num" data-testid={`${testid}-rango`}>
        {desde}–{hasta} de {total}
      </span>
      <div className="flex items-center gap-1">
        <Button type="button" size="sm" variant="outline" className={grande ? "h-10 px-3 text-sm" : "h-7 px-2"} disabled={pagina <= 1} onMouseDown={sinRobarFoco} onClick={() => onPagina(pagina - 1)} aria-label="Página anterior" data-testid={`${testid}-anterior`}>
          <ChevronLeft /> Anterior
        </Button>
        <span className="num px-1">
          {pagina} / {paginas}
        </span>
        <Button type="button" size="sm" variant="outline" className={grande ? "h-10 px-3 text-sm" : "h-7 px-2"} disabled={pagina >= paginas} onMouseDown={sinRobarFoco} onClick={() => onPagina(pagina + 1)} aria-label="Página siguiente" data-testid={`${testid}-siguiente`}>
          Siguiente <ChevronRight />
        </Button>
      </div>
    </div>
  );
}
